import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCart } from "@/contexts/CartContext";
import { useDispatch, useSelector } from "react-redux";
import {
  addTicketCarrito,
  removeTicketCarrito,
  eliminarCarrito,
  getListCarritosPorUserId,
} from "@/features/carrito/carritoThunks";

export default function CartItemTicket({
  ticket,
  eventoId,
  carritoUid,
  imgEvento,
  isUpdatingCart,
  setIsUpdatingCart,
}) {
  const dispatch = useDispatch();
  const { reloadCarts, closeCartModal } = useCart();
  const [quantity, setQuantity] = useState(ticket.cantidad || 1);
  const [isLoading, setIsLoading] = useState(false);

  // Sincronizar estado local con props cuando ticket.cantidad cambie
  useEffect(() => {
    setQuantity(ticket.cantidad || 1);
  }, [ticket.cantidad]);

  // carritos from Redux
  const { carritos } = useSelector((state) => state.carrito);
  // console.log("Rendering CartItemTicket with ticket:", ticket, "carritoUid:", carritoUid, "carritos:", carritos);
  // contar cuantos tickets tiene el carrito actual
  const currentCart = carritos.find((cart) => cart.carritoUid === carritoUid);
  const totalTicketsInCart = currentCart
    ? currentCart.tickets.reduce((sum, t) => sum + t.cantidad, 0)
    : 0;
  console.log("Total tickets in current cart:", totalTicketsInCart);

  const handleAddTicket = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setIsUpdatingCart(true);
    // Actualización optimista de la UI
    setQuantity((prev) => prev + 1);

    try {
      await dispatch(
        addTicketCarrito({
          idTipoTicket: ticket.tipoTicketId,
          precioVenta: ticket.precio,
          carritoUid,
          ticketUid: ticket.uid,
        })
      ).unwrap();

      // Recargar solo después de confirmar éxito
      reloadCarts();
    } catch (error) {
      console.error("Error al agregar ticket:", error);
      // Revertir en caso de error
      setQuantity((prev) => prev - 1);
    } finally {
      setIsLoading(false);
      setIsUpdatingCart(false);
    }
  };

  const handleRemoveTicket = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setIsUpdatingCart(true);
    // Actualización optimista de la UI
    const previousQuantity = quantity;
    setQuantity((prev) => prev - 1);

    try {
      await dispatch(
        removeTicketCarrito({
          idTipoTicket: ticket.tipoTicketId,
          precioVenta: ticket.precio,
          carritoUid,
          ticketUid: ticket.uid,
        })
      ).unwrap();

      // Recargar solo después de confirmar éxito
      reloadCarts();
    } catch (error) {
      console.error("Error al remover ticket:", error);
      // Revertir en caso de error
      setQuantity(previousQuantity);
    } finally {
      setIsLoading(false);
      setIsUpdatingCart(false);
    }
  };

  const handleDeleteCart = async () => {
    if (
      !confirm("¿Estás seguro de que quieres eliminar este ticket del carrito?")
    ) {
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    setIsUpdatingCart(true);
    try {
      await dispatch(
        removeTicketCarrito({
          idTipoTicket: ticket.tipoTicketId,
          precioVenta: ticket.precio,
          ticketUid: ticket.uid,
          carritoUid,
        })
      ).unwrap();

      reloadCarts();
      // Recargar la pagina solo si el ticket que se elimino era el ultimo ticket del carrito
      console.log("Cantidad antes de eliminar:", totalTicketsInCart);
      if (totalTicketsInCart === 1) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error al eliminar ticket:", error);
    } finally {
      setIsLoading(false);
      setIsUpdatingCart(false);
      closeCartModal();
    }
  };

  return (
    <div
      key={ticket.id}
      className="grid grid-cols-12 items-center border border-aw-bluegray p-2 mb-2 rounded-2xl"
    >
      {/* Detalle */}
      <div className="col-span-9 flex flex-col justify-between gap-2">
        <div className="flex items-center gap-3">
          <img
            src={ticket.image !== null ? ticket.image : imgEvento}
            alt=""
            className="w-10 h-10 rounded-full object-cover border border-aw-green"
          />
          <div className="flex-1 flex items-center justify-between">
            <div className="text-white">
              <p className="font-bold text-sm line-clamp-1">
                {ticket.descripcion || ticket.customTypeName}
              </p>
              <p className="text-xs text-aw-green">
                ${ticket.precio.toLocaleString()} c/u
              </p>
            </div>
            {/* Cantidad */}
            <span className="w-12 h-10 text-center flex items-center justify-center text-white bg-aw-blueblack border border-aw-gray focus:border-aw-green-75 outline-none rounded-lg px-2 py-1 text-sm">
              {quantity}
            </span>
          </div>
        </div>
      </div>

      {/* Pago */}
      <div className="col-span-3 flex items-center justify-end">
        <div className="flex flex-col justify-between gap-0.5">
          {/* Plus button */}
          <button
            onClick={handleAddTicket}
            disabled={isLoading || quantity >= (ticket.limitePorPersona || 10)}
            className={`p-1 h-8 rounded-lg border border-aw-green text-aw-green hover:bg-aw-green hover:text-aw-bluedark transition ${
              isLoading || quantity >= (ticket.limitePorPersona || 10)
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            <FontAwesomeIcon icon={"fa-solid fa-plus"} size="xs" />
          </button>
          {/* Subtotal */}
          <span className="text-aw-green font-bold text-sm text-center">
            ${(ticket.precio * quantity).toLocaleString()}
          </span>
          {/* Trash button / Minus button */}
          <button
            onClick={() =>
              quantity > 1 ? handleRemoveTicket() : handleDeleteCart()
            }
            disabled={isLoading}
            className={`p-1 h-8 rounded-lg border transition ${
              quantity > 1
                ? "border-aw-green text-aw-green hover:bg-aw-green hover:text-aw-bluedark"
                : "border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <FontAwesomeIcon
              icon={quantity > 1 ? "fa-solid fa-minus" : "fa-solid fa-trash"}
              size="xs"
            />
          </button>
        </div>
      </div>
    </div>
  );
}
